import { useEffect, useRef, useState } from 'react';
import { idiomaAtual, t } from '../i18n/idioma';
import './botoesSociais.css';

/**
 * O botão "Continuar com o Google" (techspec-login-google). É o botão oficial do Google Identity Services: ele abre o
 * popup do Google e devolve um ID token, que vai para `POST /api/auth/google`. O Client ID é público (fica no
 * navegador de qualquer jogador); o servidor confere o token contra ele.
 */
export const GOOGLE_CLIENT_ID: string =
  (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim()
  || '776155691910-lp4rv8sl6o9pf02b776auhnmdu9mr4kl.apps.googleusercontent.com';

const SCRIPT = 'https://accounts.google.com/gsi/client';

interface CredentialResponse { readonly credential?: string }
interface GoogleId {
  initialize(cfg: {
    client_id: string;
    callback: (r: CredentialResponse) => void;
    ux_mode?: 'popup' | 'redirect';
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    itp_support?: boolean;
    use_fedcm_for_button?: boolean;
  }): void;
  renderButton(el: HTMLElement, opts: Record<string, string | number>): void;
}
declare global {
  interface Window { google?: { accounts?: { id?: GoogleId } } }
}

let carregando: Promise<GoogleId> | null = null;

function carregarGis(): Promise<GoogleId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (carregando) return carregando;
  carregando = new Promise<GoogleId>((ok, erro) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.google?.accounts?.id ? ok(window.google.accounts.id) : erro(new Error('gis')));
    s.onerror = () => { carregando = null; erro(new Error('gis')); };
    document.head.appendChild(s);
  });
  return carregando;
}

/** O callback do Google é global (um por página): quem montou por último recebe o token. */
let receptor: ((credencial: string) => void) | null = null;
let iniciado = false;

export function BotaoGoogle({ texto, desligado, onCredencial }: {
  /** `continue_with` no login, `signup_with` no cadastro. */
  texto: 'continue_with' | 'signup_with' | 'signin_with';
  desligado?: boolean;
  onCredencial(credencial: string): void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [falhou, setFalhou] = useState(false);
  const atual = useRef(onCredencial);
  atual.current = onCredencial;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let vivo = true;
    void carregarGis().then(gid => {
      if (!vivo || !caixa.current) return;
      if (!iniciado) {
        gid.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: r => { if (r.credential) receptor?.(r.credential); },
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
          use_fedcm_for_button: true
        });
        iniciado = true;
      }
      const largura = Math.max(200, Math.min(400, Math.round(caixa.current.getBoundingClientRect().width || 320)));
      caixa.current.innerHTML = '';
      gid.renderButton(caixa.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        shape: 'rectangular',
        text: texto,
        logo_alignment: 'left',
        width: largura,
        locale: idiomaAtual()
      });
    }).catch(() => { if (vivo) setFalhou(true); });
    return () => { vivo = false; };
  }, [texto]);

  // Registra este botão como o dono do callback enquanto estiver na tela.
  useEffect(() => {
    const meu = (c: string) => atual.current(c);
    receptor = meu;
    return () => { if (receptor === meu) receptor = null; };
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;
  if (falhou) return <p className="footnote">{t('entrada.googleIndisponivel')}</p>;
  return (
    <div
      ref={caixa}
      className={desligado ? 'botao-google is-off' : 'botao-google'}
      aria-busy={desligado || undefined}
    />
  );
}
